import {
    type ColorVector,
    linearToOklab,
    rgbToLinear,
    rgbToOklab
} from "../../../src/helpers/color/index";

export * from "../../../src/helpers/color/index";

export type ColorMatrix = [ColorVector, ColorVector, ColorVector];

export type CvdKind = "protan" | "deutan" | "tritan";

const CVD_MATRICES: Record<CvdKind, ColorMatrix> = {
    protan: [
        [
            0.152286,
            1.052583,
            -0.204868
        ],
        [
            0.114503,
            0.786281,
            0.099216
        ],
        [
            -0.003882,
            -0.048116,
            1.051998
        ]
    ],
    tritan: [
        [
            1.255528,
            -0.076749,
            -0.178779
        ],
        [
            -0.078411,
            0.930809,
            0.147602
        ],
        [
            0.004733,
            0.691367,
            0.3039
        ]
    ],
    deutan: [
        [
            0.367322,
            0.860646,
            -0.227968
        ],
        [
            0.280085,
            0.672501,
            0.047413
        ],
        [
            -0.01182,
            0.04294,
            0.968881
        ]
    ]
};

const clampUnit = (value: number) => {
    return Math.min(Math.max(value, 0), 1);
};

const getDotProduct = (row: ColorVector, vector: ColorVector) => {
    return row[0] * vector[0] + row[1] * vector[1] + row[2] * vector[2];
};

const multiplyMatrix = (matrix: ColorMatrix, vector: ColorVector): ColorVector => {
    return [
        getDotProduct(matrix[0], vector),
        getDotProduct(matrix[1], vector),
        getDotProduct(matrix[2], vector)
    ];
};

const getOklabDistance = (first: ColorVector, second: ColorVector) => {
    return Math.hypot(first[0] - second[0], first[1] - second[1], first[2] - second[2]) * 100;
};

export const getDeltaE = (first: ColorVector, second: ColorVector) => {
    return getOklabDistance(rgbToOklab(first), rgbToOklab(second));
};

export const simulateCvd = (rgb: ColorVector, kind: CvdKind): ColorVector => {
    const linear = multiplyMatrix(CVD_MATRICES[kind], rgbToLinear(rgb));

    return [
        clampUnit(linear[0]),
        clampUnit(linear[1]),
        clampUnit(linear[2])
    ];
};

export const getCvdDeltaE = ({
    second,
    first,
    kind
}: {
    second: ColorVector;
    first: ColorVector;
    kind: CvdKind;
}) => {
    return getOklabDistance(linearToOklab(simulateCvd(first, kind)), linearToOklab(simulateCvd(second, kind)));
};
